var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import * as React from 'react';
import { EditorContext } from './contexts';
import { useAsync, mapRpcError } from './util';
import { InteractiveDiagnostics_infoToInteractive, getGoToLocation } from '@leanprover/infoview-api';
import { WithTooltipOnHover } from './tooltips';
import { marked } from 'marked';
import { RpcContext } from './rpcSessions';
import { GoalsLocation, LocationsContext, SelectableLocation } from './goalLocation';
/**
 * Core loop to display {@link TaggedText} objects. Invokes `InnerTagUi` on `tag` nodes in order to support
 * various embedded information, for example subexpression information stored in {@link CodeWithInfos}.
 * */
export function InteractiveTaggedText(_a) {
    var fmt = _a.fmt, InnerTagUi = _a.InnerTagUi;
    if ('text' in fmt)
        return React.createElement(React.Fragment, null, fmt.text);
    else if ('append' in fmt)
        return React.createElement(React.Fragment, null, fmt.append.map(function (a, i) { return React.createElement(InteractiveTaggedText, { key: i, fmt: a, InnerTagUi: InnerTagUi }); }));
    else if ('tag' in fmt)
        return React.createElement(InnerTagUi, { fmt: fmt.tag[1], tag: fmt.tag[0] });
    else
        throw new Error("malformed 'TaggedText': '".concat(fmt, "'"));
}
function Markdown(_a) {
    var contents = _a.contents;
    var renderer = new marked.Renderer();
    renderer.code = function (code, lang) {
        // todo: render Lean code blocks using the lean syntax.json
        return "<div class=\"font-code pre-wrap\">".concat(code, "</div>");
    };
    renderer.codespan = function (code) {
        return "<code class=\"font-code\">".concat(code, "</code>");
    };
    var markedOptions = {};
    markedOptions.sanitizer = function (html) {
        return '';
    };
    markedOptions.sanitize = true;
    markedOptions.silent = true;
    markedOptions.renderer = renderer;
    // todo: vscode also has lots of post render sanitization and hooking up of href clicks and so on.
    // see https://github.com/microsoft/vscode/blob/main/src/vs/base/browser/markdownRenderer.ts
    var renderedMarkdown = marked.parse(contents, markedOptions);
    return React.createElement("div", { dangerouslySetInnerHTML: { __html: renderedMarkdown } });
    // handy for debugging:
    // return <div>{ renderedMarkdown } </div>
}
/** Shows `explicitValue : itsType` and a docstring if there is one. */
function TypePopupContents(_a) {
    var info = _a.info, redrawTooltip = _a.redrawTooltip;
    var rs = React.useContext(RpcContext);
    // When `err` is defined we show the error,
    // otherwise if `ip` is defined we show its contents,
    // otherwise a 'loading' message.
    var interactive = useAsync(function () { return InteractiveDiagnostics_infoToInteractive(rs, info.info); }, [rs, info.info]);
    // We ask the tooltip parent component to relayout whenever our contents change.
    React.useEffect(function () { void redrawTooltip(); }, [interactive.state, interactive === null || interactive === void 0 ? void 0 : interactive.value, interactive === null || interactive === void 0 ? void 0 : interactive.error, redrawTooltip]);
    // Even when subexpressions are selectable in our parent component, it doesn't make sense
    // to select things inside the *type* of the parent, so we clear the context.
    // NOTE: selecting in the explicit term does make sense but it complicates the implementation
    // so let's not add it until someone really wants it.
    return React.createElement(LocationsContext.Provider, { value: undefined },
        React.createElement("div", { className: "tooltip-code-content" }, interactive.state === 'resolved' ? React.createElement(React.Fragment, null,
            React.createElement("div", { className: "font-code tl pre-wrap" },
                interactive.value.exprExplicit && React.createElement(InteractiveCode, { fmt: interactive.value.exprExplicit }),
                " : ",
                interactive.value.type && React.createElement(InteractiveCode, { fmt: interactive.value.type })),
            interactive.value.doc && React.createElement(React.Fragment, null,
                React.createElement("hr", null),
                React.createElement(Markdown, { contents: interactive.value.doc })),
            info.diffStatus && React.createElement(React.Fragment, null,
                React.createElement("hr", null),
                React.createElement("div", null, DIFF_TAG_TO_EXPLANATION[info.diffStatus]))) :
            interactive.state === 'rejected' ? React.createElement(React.Fragment, null,
                "Error: ",
                mapRpcError(interactive.error).message) :
                React.createElement(React.Fragment, null, "Loading..")));
}
var DIFF_TAG_TO_CLASS = {
    'wasChanged': 'inserted-text',
    'willChange': 'removed-text',
    'wasInserted': 'inserted-text',
    'willInsert': 'inserted-text',
    'willDelete': 'removed-text',
    'wasDeleted': 'removed-text',
};
var DIFF_TAG_TO_EXPLANATION = {
    'wasChanged': 'This subexpression has been modified.',
    'willChange': 'This subexpression will be modified.',
    'wasInserted': 'This subexpression has been inserted.',
    'willInsert': 'This subexpression will be inserted.',
    'wasDeleted': 'This subexpression has been removed.',
    'willDelete': 'This subexpression will be deleted.',
};
/**
 * Tagged spans can be hovered over to display extra info stored in the associated `SubexprInfo`.
 * Moreover if this component is rendered in a context where locations can be selected, the span
 * can be shift-clicked to select it.
 */
function InteractiveCodeTag(_a) {
    var _this = this;
    var ct = _a.tag, fmt = _a.fmt;
    var mkTooltip = React.useCallback(function (redrawTooltip) {
        return React.createElement(TypePopupContents, { info: ct, redrawTooltip: redrawTooltip });
    }, [ct.info]);
    var rs = React.useContext(RpcContext);
    var ec = React.useContext(EditorContext);
    var _b = __read(React.useState('off'), 2), hoverState = _b[0], setHoverState = _b[1];
    // We mimick the VSCode ctrl-hover and ctrl-click UI for go-to-definition
    var _c = __read(React.useState(undefined), 2), goToLoc = _c[0], setGoToLoc = _c[1];
    var fetchGoToLoc = React.useCallback(function () { return __awaiter(_this, void 0, void 0, function () {
        var lnks, loc, e_1;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (goToLoc !== undefined)
                        return [2 /*return*/, goToLoc];
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, getGoToLocation(rs, 'definition', ct.info)];
                case 2:
                    lnks = _a.sent();
                    if (lnks.length > 0) {
                        loc = { uri: lnks[0].targetUri, range: lnks[0].targetSelectionRange };
                        setGoToLoc(loc);
                        return [2 /*return*/, loc];
                    }
                    return [3 /*break*/, 4];
                case 3:
                    e_1 = _a.sent();
                    console.error('Error in go-to-definition: ', JSON.stringify(e_1));
                    return [3 /*break*/, 4];
                case 4: return [2 /*return*/, undefined];
            }
        });
    }); }, [rs, ct.info, goToLoc]);
    // Eagerly fetch the location as soon as the pointer enters this area so that we can show
    // an underline if a jump target is available.
    React.useEffect(function () { if (hoverState === 'ctrlOver')
        void fetchGoToLoc(); }, [hoverState]);
    var locs = React.useContext(LocationsContext);
    var ourLoc = locs && locs.subexprTemplate && ct.subexprPos ?
        GoalsLocation.withSubexprPos(locs.subexprTemplate, ct.subexprPos) :
        undefined;
    var spanClassName = hoverState === 'ctrlOver' && goToLoc !== undefined ? 'underline ' : '';
    if (ct.diffStatus) {
        spanClassName += DIFF_TAG_TO_CLASS[ct.diffStatus] + ' ';
    }
    return (React.createElement(WithTooltipOnHover, { mkTooltipContent: mkTooltip, onClick: function (e, next) {
            // On ctrl-click or ⌘-click, if location is known, go to it in the editor
            if (e.ctrlKey || e.metaKey) {
                setHoverState(function (st) { return st === 'over' ? 'ctrlOver' : st; });
                void fetchGoToLoc().then(function (loc) {
                    if (loc === undefined)
                        return;
                    void ec.revealPosition(__assign({ uri: loc.uri }, loc.range.start));
                });
            }
            else if (!e.shiftKey)
                next(e);
        } },
        React.createElement(SelectableLocation, { setHoverState: setHoverState, className: spanClassName, locs: locs, loc: ourLoc, alwaysHighlight: true },
            React.createElement(InteractiveCode, { fmt: fmt }))));
}
/** Displays a {@link CodeWithInfos} obtained via RPC from the Lean server. */
export function InteractiveCode(props) {
    return React.createElement("span", { className: 'font-code' },
        React.createElement(InteractiveTaggedText, __assign({}, props, { InnerTagUi: InteractiveCodeTag })));
}
